import React, { FC, useState, useRef, CSSProperties } from 'react';
import { Card, Nav, Button, Form } from 'react-bootstrap';
import SignatureCanvas from 'react-signature-canvas';
import { Nullable } from '../../../types/custom/Nullable';
import { FaBan, FaAngleDoubleRight, FaAngleDoubleLeft } from 'react-icons/fa';
import { useTextCanvas, CustomFonts } from '../../../hooks/useTextCanvas';
import { LoadingBlock } from '../../LoadingBlock';
import './SignatureCapture.scss';

interface Props {
	name: string;
	showLoading: boolean;
	onBack: () => void;
	onSubmitCallBack: (dataURL: string, isAutoGenerated: boolean) => void;
}

enum SignatureViews {
	Draw = 'draw',
	AutoGenerate = 'auto-generate'
}

const inActiveStyle: CSSProperties = {
	display: 'none'
};

const fontArray = Object.values(CustomFonts) as CustomFonts[];

export type SignatureFontTypes = CustomFonts.Paris|CustomFonts.Aurore|CustomFonts.Sacra;

const AcceptAndSign = 'Accept And Sign';
const ConfirmSignature = 'Confirm Signature';

export const SignatureCapture: FC<Props> = ({name, showLoading, onBack, onSubmitCallBack}) => {
	const [currentView, setView] = useState<SignatureViews>(SignatureViews.Draw);
	const canvasRef = useRef<Nullable<SignatureCanvas>>(null);
	const [fontStyle, setFontStyle] = useState<SignatureFontTypes>(CustomFonts.Paris);
	const [hasUserSigned, setUserSignedStatus] = useState(false);
	const { setStyle, textCanvasRef, textCanvasWidth, textCanvasHeight }  = useTextCanvas(name, CustomFonts.Paris);

	const checkIfInactive = (elementValue: SignatureViews) => {
		if (currentView !== elementValue) {
			return inActiveStyle;
		}
	};

	//////////////////////Auto Gernerate E-Signature////////////////////////////////////////////////////

	const onSubmit = () => {
		switch(currentView) {
			case SignatureViews.Draw: {
				if (canvasRef.current) {
					const generatedImg = canvasRef.current.toDataURL('image/png');
					onSubmitCallBack(generatedImg, false);
				}
				break;
			}
			case SignatureViews.AutoGenerate: {
				if(textCanvasRef.current) {
					const generatedImg = textCanvasRef.current.toDataURL('image/png');
					onSubmitCallBack(generatedImg, true);
				}
				break;
			}
		}
	};

	const handleFontChange = (event: React.BaseSyntheticEvent) => {
		setFontStyle(event.currentTarget.value as CustomFonts);
		setStyle(event.currentTarget.value as CustomFonts);
	};

	const renderFontOptions = () => fontArray.map((font) => <option key={font} value={font} className={font}>{name}</option>);

	const renderAutoGenView = () => (
		<div style={checkIfInactive(SignatureViews.AutoGenerate)}>
			<Card.Title>Generate and use an e-signature</Card.Title>
			<canvas ref={textCanvasRef} width={textCanvasWidth} height={textCanvasHeight} style={inActiveStyle}/>
			<Card.Text>
				Choose your signature style
			</Card.Text>
			<Form.Group>
				<Form.Control as="select" size="lg" value={fontStyle} onChange={handleFontChange} className={`${fontStyle} customfont-display`}>
					{renderFontOptions()}
				</Form.Control>
			</Form.Group>
			<Card.Text className="text-muted">
			By stating &quot;I agree,&quot; or by clicking <strong>{AcceptAndSign}</strong>, I consent to use this signature as my electronic signature for all generated documents, including required carrier applications and a benefit enrollment confirmation statement, same as a pen-and-paper signature.
			</Card.Text>
			{showLoading ?
				<LoadingBlock /> :
				<div className="d-flex justify-content-between mt-2">
					<Button className="btn-default" onClick={onBack}><FaAngleDoubleLeft className="mb-1" /> Back</Button>
					<Button variant="primary" onClick={onSubmit}>{AcceptAndSign} <FaAngleDoubleRight className="mb-1"/></Button>
				</div>
			}
		</div>
	);

	//////////////////////Draw your Signature////////////////////////////////////////////////////

	const checkSignature = () => canvasRef.current?.isEmpty() === false ? setUserSignedStatus(true) : setUserSignedStatus(false);

	const clearSignature = () => {
		canvasRef.current?.clear();
		setUserSignedStatus(false);
	};

	const renderDrawSignatureView = () => (
		<div style={checkIfInactive(SignatureViews.Draw)}>
			<Card.Title>Draw your signature</Card.Title>
			<SignatureCanvas penColor='black' canvasProps={{ width: 500, height: 200, className: 'signature-canvas mt-1' }} ref={canvasRef} onEnd={checkSignature} />
			<br/>
			<Card.Text className="text-muted">
				By clicking <strong>{ConfirmSignature}</strong>, I consent to use this signature as my electronic signature for all generated documents, including required carrier applications and a benefit enrollment confirmation statement, same as a pen-and-paper signature.
			</Card.Text>
			{showLoading ?
				<LoadingBlock /> :
				<div className="d-flex justify-content-between mt-2">
					<Button className="btn-default mr-2" onClick={onBack}><FaAngleDoubleLeft className="mb-1" /> Back</Button>
					<Button variant="warning" onClick={clearSignature}><FaBan className="mb-1"/> Clear Signature</Button>
					<Button variant="primary" className="ml-2" disabled={!hasUserSigned} onClick={onSubmit}>{ConfirmSignature} <FaAngleDoubleRight className="mb-1"/></Button>
				</div>
			}
		</div>
	);

	//////////////////////View Control////////////////////////////////////////////////////

	const renderCotent = () => (
		<Card.Body>
			{renderDrawSignatureView()}
			{renderAutoGenView()}
		</Card.Body>
	);

	const renderHeader = () => (
		<Card.Header>
			<Nav variant="tabs" activeKey={currentView}>
				<Nav.Item>
					<Nav.Link eventKey="draw" onClick={() => setView(SignatureViews.Draw)}>Draw</Nav.Link>
				</Nav.Item>
				<Nav.Item>
					<Nav.Link eventKey="auto-generate" onClick={() => setView(SignatureViews.AutoGenerate)}>E-Signature</Nav.Link>
				</Nav.Item>
			</Nav>
		</Card.Header>
	);

	return (
		<Card>
			{renderHeader()}
			{renderCotent()}
		</Card>
	);
};
